import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Card, Flex, Icon, Input, Popover, Text, useElement, useOuterEvent } from '@pega/cosmos-react-core';
import { StyledTable } from './custom.styled';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import { countryCodesAndNames, countryCodesAndNumbers, countryNamesAndCodes } from '../../constants/countryCodes';
import useLocalTranslation from '../../hooks/useLocalTranslation';
interface AutoCompleteProps {
  countryCodes: { [country: string]: string };
  setCountryCodes: (codes: { [country: string]: string }) => void;
}
const CountryCodeAutoComplete: React.FC<AutoCompleteProps> = ({ countryCodes, setCountryCodes }: AutoCompleteProps) => {
  const [value, setValue] = useState('');
  const [didUpdate, setDidUpdate] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showCountryCodes, setShowCountryCodes] = useState(false);
  const [countryCodesBtnEl, setCountryCodesBtnEl] = useElement();
  const [countryCodesPopoverEl, setCountryCodesPopoverEl] = useElement();
  const translate = useLocalTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDidUpdate(true);
    setValue(event.target.value);
    const newSuggestions = Object.keys(countryCodesAndNames).filter(
      (country) =>
        countryCodesAndNames[country].toLowerCase().includes(event.target.value.toLowerCase()) &&
        !Object.keys(countryCodes).includes(country),
    );
    setSuggestions(newSuggestions);
    if (!showCountryCodes) {
      setShowCountryCodes(true);
    }
  };

  useOuterEvent('mousedown', [countryCodesPopoverEl, countryCodesBtnEl], () => {
    setShowCountryCodes(false);
  });
  const onCountryCodeSelect = (country: string) => {
    setValue(countryCodesAndNames[country]);
    setShowCountryCodes(false);
  };

  const onAddCountryCode = () => {
    const countryCharCode = countryNamesAndCodes[value];
    if (!Object.keys(countryCodes).includes(countryCharCode)) {
      const newCountryCodes = {
        ...countryCodes,
        [countryCharCode]: countryCodesAndNumbers[countryCharCode].replaceAll('+', ''),
      };
      setCountryCodes(newCountryCodes);
    }
    setValue('');
  };

  useEffect(() => {
    const newSuggestions = Object.keys(countryCodesAndNumbers).filter(
      (country) => !Object.keys(countryCodes).includes(country),
    );
    setSuggestions(newSuggestions);
  }, [countryCodes]);

  const compareCountries = (a: string, b: string) => {
    if (countryCodesAndNames[a] < countryCodesAndNames[b]) {
      return -1;
    }
    if (countryCodesAndNames[a] > countryCodesAndNames[b]) {
      return 1;
    }
    return 0;
  };

  const getColumnData = useMemo(() => {
    const removeCountry = (country: string) => {
      const newCountryCodes = { ...countryCodes };
      delete newCountryCodes[country];
      setCountryCodes(newCountryCodes);
    };

    return Object.keys(countryCodes)
      .sort(compareCountries)
      .map((country, index) => {
        return {
          id: index,
          channel: <Text>{countryCodesAndNames[country]}</Text>,
          connection: (
            <>
              <Flex
                container={{
                  direction: 'row',
                  justify: 'between',
                  alignItems: 'center',
                }}
              >
                <Text>{`+${countryCodesAndNumbers[country]}`}</Text>
                <Button
                  variant="simple"
                  icon
                  onClick={() => {
                    removeCountry(country);
                  }}
                >
                  <Icon name="trash" />
                </Button>
              </Flex>
            </>
          ),
        };
      });
  }, [countryCodes, setCountryCodes]);

  const columns: ColumnProps<typeof getColumnData[number]>[] = [
    { renderer: 'channel', label: 'Country' },
    { renderer: 'connection', label: 'Code' },
  ];
  const error =
    (didUpdate && Object.keys(countryCodes).length === 0) ||
    (value !== '' && !Object.keys(countryNamesAndCodes).includes(value));
  const CountryCodes = () => {
    return (
      <Flex container={{ direction: 'column' }} style={{ width: '100%' }}>
        {suggestions.map((country) => (
          <Button
            key={country}
            onClick={() => onCountryCodeSelect(country)}
            style={{ border: 'none', borderRadius: 0, marginInlineStart: 0 }}
          >
            <Flex
              style={{ width: '100%' }}
              container={{
                itemGap: 1,
                justify: 'between',
                alignItems: 'end',
              }}
            >
              {`${countryCodesAndNames[country]}`}
              <Text style={{ color: 'grey' }}>{`+${countryCodesAndNumbers[country]}`}</Text>
            </Flex>
          </Button>
        ))}
        {value !== '' && suggestions.length === 0 && (
          <Button
            style={{
              border: 'none',
              borderRadius: 0,
              marginInlineStart: 0,
              background: 'none',
            }}
          >
            {translate('no_country_found')}
          </Button>
        )}
      </Flex>
    );
  };
  return (
    <Flex container={{ direction: 'column', itemGap: 1.5 }} style={{ width: '100%' }}>
      <Text variant="h3">{translate('allowed_country_codes')}</Text>
      <Text>{translate('country_codes_box_info_text')}</Text>
      <Text>
        <b>{translate('note')}:</b> {translate('country_codes_note')}
      </Text>
      <Flex
        style={{ width: '100%' }}
        container={{
          itemGap: 1,
          justify: 'between',
          alignItems: error ? 'center' : 'end',
        }}
      >
        <Flex style={{ width: '85%', height: 'auto' }}>
          <Input
            label={translate('add_country')}
            autoComplete="no"
            value={value}
            onChange={handleChange}
            ref={setCountryCodesBtnEl}
            onFocus={() => {
              setShowCountryCodes(true);
              setDidUpdate(true);
            }}
            info={error && translate('country_code_dropdown_error_info')}
            status={error ? 'error' : undefined}
          />
          {showCountryCodes && (
            <Popover
              ref={setCountryCodesPopoverEl}
              target={countryCodesBtnEl}
              placement="bottom"
              style={{
                width: countryCodesBtnEl?.clientWidth,
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              <CountryCodes />
            </Popover>
          )}
        </Flex>
        <Button onClick={() => onAddCountryCode()} disabled={!Object.keys(countryNamesAndCodes).includes(value)}>
          {translate('add')}
        </Button>
      </Flex>
      <Card style={{ padding: 0 }}>
        <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={getColumnData} />
      </Card>
    </Flex>
  );
};

export default CountryCodeAutoComplete;
